import { apiErrorMessage } from '@agos/api-client'
import {
  getGetAiTranslateStatusQueryKey,
  useGetAiTranslateJobId,
  useGetAiTranslateStatus,
  useGetAiTranslationUsage,
  usePostAiTranslateAllProducts,
  usePostAiTranslateCategories,
} from '@agos/api-client/ai'
import {
  useGetTranslationsJobId,
  useGetTranslationsStats,
  usePostTranslationsTranslateAllMissing,
} from '@agos/api-client/marketing'
import { usePutSettingsBasic } from '@agos/api-client/platform'
import { T, formatCount, formatPercent, tt, useAuth } from '@agos/shared'
import { ConfirmDialog, PageHeader, Spinner, cn, useToast, BUTTON_PRIMARY} from '@agos/ui'

import { TranslationUsagePanel } from './TranslationUsagePanel'
import {
  CONFIRM_WORD,
  confirmsSweep,
  costPerCall,
  estimatedCost,
  forceSweepPayload,
  sweepEstimateNote,
  sweepScope,
  sweepScopeNote,
} from './force-sweep'
import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, KeyRound, Languages, Save, Sparkles } from 'lucide-react'
import { useMemo, useState } from 'react'

import { mayWrite, readOnlyReason } from '../write-access'
import {
  JOB_POLL_MS,
  jobFinished,
  jobGaveUp,
  jobOf,
  jobSummary,
  autoNote,
  bulkConfirmation,
  bulkLabel,
  formOf,
  formProblem,
  formProblemMessage,
  keyMessage,
  keyState,
  languageChoices,
  payloadOf,
  statusOf,
  toggleLanguage,
  type AiForm,
  type BulkKind,
} from './ai-translate'

/**
 * `/settings-ai-translate` — which languages the machine translates into, and two bulk runs.
 *
 * **FB-161 (security).** Legacy reads `GET /settings/basic` — FB-132's endpoint, which used to answer a live
 * Anthropic API key to any `admin` — for the single purpose of computing `!!b.anthropicApiKey`.
 * `GET /ai/translate-status` answers `hasApiKey` and carries no key, so that is what this page reads. The
 * credential does not reach this screen at all.
 *
 * That endpoint no longer answers the key either (`MIGRATION-STATUS`, 2026-08-02) — it sends
 * `anthropicApiKeySet`. Reading the purpose-built status endpoint is still the right choice: it is the one
 * that also knows whether the key *works*.
 *
 * **FB-162.** `hasApiKey` started as `true`, so the page stated that a key was configured before anything was
 * known. There are three states here and *unknown* is one of them.
 *
 * **FB-163.** One bulk run asked with a `window.confirm` and the other fired on the click, though both spend
 * tokens per item. Both are confirmed now, with the language count and the cost. And `pollJob`'s `while (true)`
 * is bounded — legacy polls every 1,5 seconds for as long as the tab is open if a job never reports `done`.
 *
 * Ported from legacy `pages/settings-ai-translate.tsx` (312 lines).
 */

export function SettingsAiTranslatePage() {
  const query = useGetAiTranslateStatus()

  if (query.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }

  return <AiTranslateEditor status={statusOf(query.data)} />
}

function AiTranslateEditor({ status }: { status: ReturnType<typeof statusOf> }) {
  const { toast } = useToast()
  const { user, hasPermission } = useAuth()
  const queryClient = useQueryClient()

  const [form, setForm] = useState<AiForm>(() => formOf(status))
  const [confirming, setConfirming] = useState<BulkKind | null>(null)
  // §9.325 - the typed word, local to the section that reads it.
  const [forceTyped, setForceTyped] = useState('')
  const [lastRun, setLastRun] = useState<{ kind: BulkKind; jobId: string } | null>(null)
  /**
   * §9.48 — watch the run rather than announcing it and looking away.
   *
   * **Bounded**, which is what `jobGaveUp` is for: the note above records that legacy polls every
   * 1,5 s for as long as the tab is open, so a page left open against a job that died is a request
   * every second and a half until somebody closes it. It stops on a terminal status too, so a
   * finished run costs one last request.
   */
  const [attempts, setAttempts] = useState(0)
  const gaveUp = jobGaveUp(attempts)
  const { data: jobData, dataUpdatedAt } = useGetAiTranslateJobId(lastRun?.jobId ?? '', {
    query: {
      enabled: lastRun !== null && lastRun.kind !== 'strings' && !gaveUp,
      refetchInterval: (query: { state: { data?: unknown } }) =>
        jobFinished(jobOf(query.state.data)) ? false : JOB_POLL_MS,
    },
  })
  /**
   * §9.305 - the strings sweep reports on its own endpoint, so it needs its own poll.
   *
   * Two hooks rather than one because they are two paths: `/ai/translate-job/{id}` for the product
   * and category runs, `/translations/job/{id}` for this one. Each is enabled only for the run it
   * belongs to, so exactly one is ever asking - and both are bounded by the same `gaveUp`.
   */
  const { data: sweepData, dataUpdatedAt: sweepUpdatedAt } = useGetTranslationsJobId(
    lastRun?.jobId ?? '',
    {
      query: {
        enabled: lastRun !== null && lastRun.kind === 'strings' && !gaveUp,
        refetchInterval: (query: { state: { data?: unknown } }) =>
          jobFinished(jobOf(query.state.data)) ? false : JOB_POLL_MS,
      },
    },
  )
  const job =
    lastRun === null ? null : jobOf(lastRun.kind === 'strings' ? sweepData : jobData)
  /**
   * One per **answer**, keyed on when the answer arrived.
   *
   * The first version incremented whenever the job was running, which is a render-phase update whose
   * own condition is still true on the next render: it span straight to the 400 cap, gave up, and
   * disabled the poll before the job could report `done`. The e2e caught it because it waits for a
   * terminal status rather than for the counter.
   */
  const [countedAt, setCountedAt] = useState(0)
  /**
   * Whichever poll is actually running - §9.305.
   *
   * This counted `dataUpdatedAt` alone, and the strings run polls the other endpoint: its
   * `dataUpdatedAt` stays 0 because that hook is disabled, so the counter never moved, `gaveUp` never
   * fired, and the new sweep polled **without a bound** - which is precisely the defect §9.48 wrote
   * `jobGaveUp` to stop. Adding a third run to a page with two pollers is how a cap silently stops
   * applying.
   */
  const answeredAt = lastRun?.kind === 'strings' ? sweepUpdatedAt : dataUpdatedAt
  if (lastRun !== null && answeredAt !== 0 && answeredAt !== countedAt) {
    setCountedAt(answeredAt)
    setAttempts((current) => current + 1)
  }

  /* §9.758, BACKEND.md §10A.413 - two rules on one page. The save is `PUT /settings/basic`, which gives
     `autoTranslateEnabled`, `autoTranslateLanguages` and `defaultLanguage` to `translations_admin`. The three runs
     below - `POST /ai/translate-all-products`, `/ai/translate-categories` and `/translations/translate-all-missing` -
     are `{main_admin, admin}`, the translation memory's own rule, so they ask that rather than the page's right. */
  const writable = mayWrite('ai-translate', user?.systemRole, hasPermission)
  const readOnly = readOnlyReason('ai-translate', user?.systemRole, hasPermission)
  const runnable = mayWrite('translations', user?.systemRole, hasPermission)
  const state = keyState(status)
  const problem = formProblem(form)
  const choices = languageChoices(status)

  const save = usePutSettingsBasic({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetAiTranslateStatusQueryKey() })
        toast({ title: tt('Nastavenia AI prekladu uložené') })
      },
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Uloženie zlyhalo'),
          description: apiErrorMessage(caught, tt('Na serveri sa nič nezmenilo.')),
        }),
    },
  })

  const started = (kind: BulkKind) => (answer: unknown) => {
    const jobId = (answer as { jobId?: unknown } | undefined)?.jobId
    if (typeof jobId === 'string' && jobId !== '') {
      setLastRun({ kind, jobId })
      toast({
        title: tt('Preklad beží na pozadí'),
        description: tt('Priebeh nájdete v Prekladoch - táto obrazovka ho nesleduje.'),
      })
      return
    }
    toast({
      variant: 'destructive',
      title: tt('Preklad sa nespustil'),
      description: tt('Server nevrátil číslo behu.'),
    })
  }

  // §9.395 - *Server ho odmietol* says only that it was refused. Which refusal it was is the part
  // somebody can act on, and the server sends it.
  const failed = (caught: unknown) =>
    toast({
      variant: 'destructive',
      title: tt('Preklad sa nespustil'),
      description: apiErrorMessage(caught, tt('Server ho odmietol.')),
    })

  const products = usePostAiTranslateAllProducts({
    mutation: { onSuccess: started('products'), onError: failed },
  })
  const categories = usePostAiTranslateCategories({
    mutation: { onSuccess: started('categories'), onError: failed },
  })
  /**
   * §9.305 - the sweep over `translation_memory`, which is the interface itself.
   *
   * **No body.** The endpoint defaults `targetLangs` to the franchise's configured languages, which
   * is what this screen would send anyway, and sending them again would put the same decision in two
   * places. `forceRetranslate` is left off deliberately: it re-buys every automatic translation the
   * franchise already owns, which is a bill rather than a preference, and is banked for the owner.
   */
  const strings = usePostTranslationsTranslateAllMissing({
    mutation: { onSuccess: started('strings'), onError: failed },
  })

  const running = products.isPending || categories.isPending || strings.isPending

  /**
   * §9.325 - what a forced sweep would send and cost, from two endpoints that already answer it.
   *
   * `range=all` deliberately: the default is the current month and this franchise made **no** calls
   * this month, so the month's figures would have quoted an estimate of nothing. A lifetime average
   * over 18 016 calls is the figure that is not a guess.
   */
  const stats = useGetTranslationsStats()
  const lifetime = useGetAiTranslationUsage({ range: 'all' })
  const forceScope = useMemo(() => {
    const payload = (stats.data ?? {}) as {
      uniqueSources?: number
      activeLangs?: unknown
      perLang?: unknown
    }
    const perLang = Array.isArray(payload.perLang)
      ? (payload.perLang as Record<string, unknown>[]).map((row) => ({
          lang: typeof row.lang === 'string' ? row.lang : '',
          manual: typeof row.manual === 'number' ? row.manual : 0,
          untranslatable: typeof row.untranslatable === 'number' ? row.untranslatable : 0,
        }))
      : []
    const active = Array.isArray(payload.activeLangs)
      ? (payload.activeLangs as unknown[]).filter((lang): lang is string => typeof lang === 'string')
      : []
    return sweepScope(payload.uniqueSources ?? 0, perLang, active)
  }, [stats.data])
  const forceCost = useMemo(() => {
    const totals = (lifetime.data as { totals?: { totalCalls?: number; costEur?: number } } | undefined)
      ?.totals
    return estimatedCost(
      forceScope.rows,
      costPerCall(totals?.totalCalls ?? 0, totals?.costEur ?? 0),
    )
  }, [lifetime.data, forceScope.rows])

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('AI preklad')}
        description={tt('Do ktorých jazykov sa prekladá a čo to stojí')}
        icon={<Sparkles className="w-5 h-5" />}
        actions={
          <button
            type="button"
            data-save
            disabled={!writable || save.isPending || problem !== null}
            onClick={() => save.mutate({ data: { ...payloadOf(form) } })}
            className={cn('flex items-center gap-2 px-4 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            {save.isPending ? <Spinner /> : <Save className="w-4 h-4" />} <T>Uložiť</T>
          </button>
        }
      />

      {readOnly !== null && (
        <p className="text-[11px] text-amber-500" data-read-only>
          {readOnly}
        </p>
      )}

      {/* FB-161 and FB-162: whether a key exists, from an endpoint that does not carry it. */}
      <p
        className={cn(
          'text-xs flex items-start gap-2',
          state === 'present' ? 'text-muted-foreground' : 'text-amber-500',
        )}
        data-key={state}
      >
        <KeyRound className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        {keyMessage(state)}
      </p>

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="flex items-start justify-between px-5 py-4 gap-4" data-toggle="autoTranslate">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-sm">
              <T>Automatický preklad nových textov</T>
            </p>
            <p className="text-xs text-muted-foreground mt-0.5" data-auto-note>
              {autoNote(form, state)}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={form.autoEnabled}
            aria-label={tt('Automatický preklad nových textov')}
            disabled={!writable}
            data-toggle-state={form.autoEnabled ? 'on' : 'off'}
            onClick={() => setForm((current) => ({ ...current, autoEnabled: !current.autoEnabled }))}
            className={cn(
              'w-11 h-6 rounded-full relative shrink-0 transition-colors disabled:opacity-50',
              form.autoEnabled ? 'bg-primary' : 'bg-muted',
            )}
          >
            <span
              className={cn(
                'absolute top-0.5 w-5 h-5 rounded-full bg-background transition-all',
                form.autoEnabled ? 'left-[22px]' : 'left-0.5',
              )}
            />
          </button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-5 space-y-3">
        <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
          <T>Jazyky</T>
        </h2>
        <div className="flex flex-wrap gap-2">
          {choices.map((choice) => {
            const selected = form.languages.includes(choice.code)
            return (
              <button
                key={choice.code}
                type="button"
                data-language={choice.code}
                data-language-state={selected ? 'on' : 'off'}
                data-supported={choice.supported ? 'yes' : 'no'}
                aria-pressed={selected}
                disabled={!writable}
                onClick={() => setForm((current) => toggleLanguage(current, choice.code))}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium disabled:opacity-50',
                  selected
                    ? 'bg-primary/15 border-primary/40 text-primary'
                    : 'bg-muted border-border text-muted-foreground',
                  !choice.supported && 'line-through opacity-60',
                )}
              >
                {choice.flag} {choice.label}
                {choice.code === form.defaultLanguage && (
                  <span className="text-[10px] font-normal">
                    (<T>zdroj</T>)
                  </span>
                )}
              </button>
            )
          })}
        </div>
        {choices.some((choice) => !choice.supported) && (
          <p className="text-[11px] text-amber-500" data-unsupported>
            <T>Prečiarknuté jazyky server momentálne nepodporuje.</T>
          </p>
        )}
        {problem !== null && (
          <p className="text-[11px] text-red-500" data-form-problem={problem}>
            {formProblemMessage(problem)}
          </p>
        )}
      </div>

      {/* §9.255 - above the buttons that spend, because a cost report on another screen is one
        * nobody consults before pressing. This page's own header has always said *"a čo to stojí"*;
        * the cost half was never built. */}
      <TranslationUsagePanel />

      <div className="bg-card border border-border rounded-2xl p-5 space-y-3">
        <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
          <T>Hromadný preklad</T>
        </h2>
        <p className="text-xs text-muted-foreground">
          <T>
            Prejde existujúce texty a doplní chýbajúce preklady. Beží na pozadí a účtuje sa za každý preklad.
          </T>
        </p>
        <div className="flex flex-wrap gap-3">
          {(['products', 'categories', 'strings'] as const).map((kind) => (
            <button
              key={kind}
              type="button"
              data-bulk={kind}
              disabled={!runnable || running || state !== 'present' || problem !== null}
              onClick={() => setConfirming(kind)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border border-border bg-muted text-sm font-medium disabled:opacity-50"
            >
              {running ? <Spinner /> : <Languages className="w-4 h-4" />} {bulkLabel(kind)}
            </button>
          ))}
        </div>
        {!runnable && (
          <p className="text-[11px] text-amber-500" data-bulk-read-only>
            <T>Hromadný preklad aj preklad odznova spúšťa len administrátor - každý preklad sa účtuje.</T>
          </p>
        )}
        {lastRun !== null && (
          <div className="space-y-1" data-last-run={lastRun.kind}>
            <p className="text-xs text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
              <T>Beh spustený</T>: <span className="font-mono">{lastRun.jobId}</span>
            </p>
            {job !== null && (
              <p className="text-xs" data-job-progress={job.status}>
                <span className="tabular-nums">{jobSummary(job)}</span>
                {job.message !== null && (
                  <span className="text-muted-foreground"> · {job.message}</span>
                )}
              </p>
            )}
            {gaveUp && (
              // Said plainly rather than left spinning: this screen stopped watching, which is not
              // the same as the job having stopped.
              <p className="text-[11px] text-amber-500" data-job-gave-up>
                <T>Priebeh sa už nesleduje - otvorte Preklady, ak beh stále trvá.</T>
              </p>
            )}
          </div>
        )}
        {state !== 'present' && (
          <p className="text-[11px] text-amber-500 flex items-center gap-1.5" data-bulk-blocked>
            <AlertTriangle className="w-3 h-3 shrink-0" />
            {keyMessage(state)}
          </p>
        )}
      </div>

      {/* §9.325, the owner's A8 - *"add it, with a cost estimate and a typed confirmation."* §9.305
          left `forceRetranslate` off and said why: *"a bill rather than a preference, and is banked
          for the owner."* Banked, and answered.

          **Its own section, below the three that fill gaps**, because it is a different action: those
          three send what is missing and this one sends everything again. One button among four would
          read as a fourth flavour of the same thing. */}
      <div
        className="bg-card border border-amber-500/40 rounded-2xl p-5 space-y-3"
        data-force-sweep
      >
        <h2 className="text-xs font-bold text-amber-500 uppercase tracking-wider">
          <T>Preložiť všetko odznova</T>
        </h2>
        <p className="text-xs text-muted-foreground">
          <T>
            Nahradí každý automatický preklad novým. Použite, keď sa zmenil zdrojový text alebo
            nie ste spokojní s kvalitou.
          </T>
        </p>
        <p className="text-xs text-foreground" data-force-estimate>
          {sweepEstimateNote(forceScope, forceCost)}
        </p>
        <p className="text-[11px] text-muted-foreground" data-force-scope>
          {sweepScopeNote(forceScope)}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={forceTyped}
            onChange={(change) => setForceTyped(change.target.value)}
            aria-label={`${tt('Napíšte')} ${CONFIRM_WORD}`}
            placeholder={CONFIRM_WORD}
            data-force-confirm
            disabled={!runnable || running || state !== 'present'}
            className="min-h-[36px] w-40 px-3 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
          />
          <button
            type="button"
            data-force-run
            disabled={
              !runnable ||
              running ||
              state !== 'present' ||
              problem !== null ||
              !confirmsSweep(forceTyped) ||
              forceScope.rows === 0
            }
            onClick={() => {
              setForceTyped('')
              strings.mutate({ data: { ...forceSweepPayload() } })
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-amber-500/60 text-amber-500 text-sm font-semibold disabled:opacity-40"
          >
            {running ? <Spinner /> : <Languages className="w-4 h-4" />}{' '}
            <T>Preložiť všetko odznova</T>
          </button>
        </div>
        {/* No `ConfirmDialog` beside the typed word. A dialog after typing eight letters is a second
            gate over one decision, and the three buttons above already use the dialog for the cheap
            action - having both here would make the expensive one feel the same as those. */}
        <p className="text-[11px] text-muted-foreground" data-force-unstoppable>
          <T>Beh sa nedá zastaviť. Odhad vychádza z vašich vlastných doterajších prekladov.</T>
        </p>
      </div>

      <p className="text-[11px] text-muted-foreground" data-source-note>
        <T>Zdrojový jazyk je ten, z ktorého sa prekladá - v zozname je označený.</T>{' '}
        {form.languages.length > 1 && (
          <>
            <T>Cieľových jazykov</T>: {formatCount(form.languages.length - 1)} (
            {formatPercent(((form.languages.length - 1) / Math.max(1, choices.length - 1)) * 100, 0)}{' '}
            <T>z katalógu</T>).
          </>
        )}
      </p>

      <ConfirmDialog
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        variant="danger"
        title={tt('Spustiť hromadný preklad?')}
        description={confirming === null ? '' : bulkConfirmation(confirming, form)}
        confirmLabel={tt('Spustiť')}
        icon={<Sparkles className="w-5 h-5" />}
        loading={running}
        onConfirm={() => {
          const kind = confirming
          setConfirming(null)
          if (kind === 'products') products.mutate({ data: { onlyMissing: true } })
          else if (kind === 'categories') categories.mutate()
          else if (kind === 'strings') strings.mutate({ data: {} })
        }}
      />
    </div>
  )
}
